import React, { useState, useCallback, useEffect } from "react";
import CoreCard from "../../../../app/core/components/CoreCard/CoreCard";
import CoreTextArea from "../../../../app/core/components/CoreTextArea/CoreTextArea";
import CoreButton from "../../../../app/core/components/CoreButton/CoreButton";
import { Box, Divider, InputAdornment } from "@mui/material";
import CoreInputField from "../../../../app/core/components/CoreInputField/CoreInputField";
import CoreButtonIcon from "../../../../app/core/components/CoreButtonIcon/CoreButtonIcon";
import CoreSpinner from "../../../../app/core/components/CoreSpinner/CoreSpinner";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getUploadLogoURL, doUploadLogoFileOnS3 } from "./gestisciStudioSlice";
import { useNavigate } from "react-router-dom";
import { actions as appActions } from "../../../../app/appSlice";
import Dropzone from "react-dropzone";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import { LOGO_UPLOAD_MAX_SIZE } from "../../../../utils/constants";
import {
  useGetStudioIdQuery,
  useSaveStudioMutation,
} from "../../../api/admin/gestisciStudioApi";
import ApartmentIcon from "@mui/icons-material/Apartment";
import SignpostIcon from "@mui/icons-material/Signpost";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import LanguageIcon from "@mui/icons-material/Language";
import { useTranslation } from "react-i18next";
import CoreIconDelete from "../../../../app/core/components/CoreIconDelete/CoreIconDelete";
import CoreBack from "../../../../app/core/components/CoreBack/CoreBack";

const PersonalizzaStudio = () => {
  const baseColors = useSelector((state) => state.app.appConfig.assets.colors);
  const appConfig = useSelector((state) => state.app.appConfig);
  const studioId = useSelector((state) => state.app.studioId);
  const { t } = useTranslation();
  const [formData, setFormData] = useState({});
  const [isNewLogoDropped, setIsNewLogoDropped] = useState(false);

  const { data, isLoading: loading } = useGetStudioIdQuery(studioId);
  const [saveStudio, { isLoading: isLoadingEdit }] = useSaveStudioMutation();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (data) {
      setFormData((prevValue) => ({
        ...prevValue,
        name: data?.Data?.name || "",
        email: data?.Data?.email || "",
        address: data?.Data?.address || "",
        phoneNumber: data?.Data?.phoneNumber || "",
        vat: data?.Data?.vat || "",
        dropLogo: data?.Data?.logoImageUrl,
        description: data?.Data?.description,
        primaryColor: data?.Data?.primaryColor,
        secondaryColor: data?.Data?.secondaryColor,
        website: data?.Data?.website || "",
      }));
    }
  }, [data, baseColors]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleResetColor = () => {
    setFormData((prevValue) => ({
      ...prevValue,
      primaryColor: null,
      secondaryColor: null,
    }));
  };

  const onDrop = useCallback(
    (acceptedFiles) => {
      acceptedFiles.forEach((file) => {
        const logoFile = file;
        setFormData((prevValue) => ({
          ...prevValue,
          logoFile: logoFile,
        }));
        const reader = new FileReader();
        reader.onload = () => {
          if (file.size > LOGO_UPLOAD_MAX_SIZE) {
            toast.warning(t("InvalidFileSize"));
          } else {
            setIsNewLogoDropped(true);
            setFormData((prevValue) => ({
              ...prevValue,
              dropLogo: reader.result,
            }));
          }
        };
        reader.readAsDataURL(file);
      });
    },
    [t]
  );

  const uploadLogo = async () => {
    let imgUrl = "";
    await dispatch(getUploadLogoURL()).then(async (logoResponse) => {
      if (logoResponse && logoResponse.payload && logoResponse.payload.Data) {
        const s3_endpoint = logoResponse.payload.Data;
        const questionMarkIndex = s3_endpoint.indexOf("?");
        const urlBeforeQuestionMark = s3_endpoint.substring(
          0,
          questionMarkIndex
        );
        imgUrl = urlBeforeQuestionMark;
        await dispatch(
          doUploadLogoFileOnS3({
            url: s3_endpoint,
            file: new Blob([formData.logoFile]),
            type: formData.logoFile.type,
          })
        ).then(() => {
          setFormData({ ...formData, logoImageUrl: urlBeforeQuestionMark });
        });
      }
    });
    return imgUrl;
  };

  const deleteLogo = () => {
    setIsNewLogoDropped(false);
    setFormData((prevValue) => ({
      ...prevValue,
      dropLogo: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const id = toast.loading(t("Saving..."), {
      type: "info",
    });

    let newLogoUploaded = formData.dropLogo;
    if (isNewLogoDropped) newLogoUploaded = await uploadLogo();

    const studioData = {
      Id: studioId,
      logoImageUrl: newLogoUploaded,
      ...formData,
    };

    saveStudio(studioData)
      .unwrap()
      .then((res) => {
        if (res.Result !== 1) {
          toast.update(id, {
            render: res.Exception || t("SavingNotOk"),
            type: "error",
            autoClose: 1500,
            isLoading: false,
          });
          return;
        }

        toast.update(id, {
          render: `${t("StudioUpdated")}`,
          type: "success",
          autoClose: 1500,
          isLoading: false,
        });

        dispatch(
          appActions.updateStudioName({
            studioName: formData.name,
          })
        );
        dispatch(
          appActions.setColors({
            primaryColor: studioData.primaryColor,
            secondaryColor: studioData.secondaryColor,
          })
        );
        dispatch(
          appActions.setLogoImageURL({
            logoImageURL: studioData.logoImageUrl,
          })
        );
      })
      .catch(() => {
        toast.update(id, {
          render: t("SavingNotOk"),
          type: "error",
          autoClose: 1500,
          isLoading: false,
        });
      });
  };

  if (loading) {
    return <CoreSpinner />;
  }

  return (
    <div className="flex flex-col h-full w-full">
      <div className="flex items-center w-full">
        <CoreBack />
        <h2 className="w-full">{`${t("Customize")} ${t("studio")}`}</h2>
      </div>
      <div className="flex flex-row mx-auto w-11/12 space-x-24 justify-between">
        <div className="flex flex-col w-full justify-start  px-12 ">
          <div className="flex flex-row items-center space-x-[10%]">
            <div className="flex-row flex items-center my-12 space-x-8">
              <div className="flex">
                <CoreCard
                  disabled={appConfig.modules.logoPlaceHolder}
                  className="relative"
                  width={"10rem"}
                  height={"10rem"}
                  child={
                    appConfig?.modules?.logoPlaceHolder ? (
                      <>
                        <img
                          alt="logo"
                          src={appConfig?.assets?.imgs?.logo}
                          className="w-full h-full"
                        />
                      </>
                    ) : formData.dropLogo ? (
                      <>
                        <CoreIconDelete onDelete={deleteLogo} />
                        <img
                          alt="logo"
                          src={formData.dropLogo}
                          className="w-full h-full p-2"
                        />
                      </>
                    ) : (
                      <Dropzone onDrop={onDrop}>
                        {({ getRootProps, getInputProps }) => (
                          <div
                            {...getRootProps()}
                            style={{
                              paddingTop: "20px",
                              textAlign: "center",
                              cursor: "pointer",
                            }}
                          >
                            <input {...getInputProps()} />
                            <Box display="flex" flexDirection="column">
                              <CoreButtonIcon
                                icon={
                                  <UploadFileRoundedIcon
                                    className="text-gray"
                                    sx={{ fontSize: 70 }}
                                  />
                                }
                              />
                            </Box>
                          </div>
                        )}
                      </Dropzone>
                    )
                  }
                />
              </div>
            </div>
          </div>
          <Divider className="my-8" />
          <div className="flex flex-row justify-between space-x-12">
            <div className="flex flex-col w-1/2">
              <h3 className="font-bold m-0">{t("InsertStudioData")}</h3>
              <CoreInputField
                disabled={appConfig.modules.logoPlaceHolder}
                name="name"
                label={`${t("Name")} ${t("studio")}`}
                type="text"
                value={formData.name}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ApartmentIcon className="text-gray" />
                    </InputAdornment>
                  ),
                }}
              />
              <CoreInputField
                disabled={appConfig.modules.logoPlaceHolder}
                name="address"
                label={t("Address")}
                value={formData.address}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SignpostIcon className="text-gray" />
                    </InputAdornment>
                  ),
                }}
              />
              <CoreInputField
                name="email"
                label="Email"
                value={formData.email}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon className="text-gray" />
                    </InputAdornment>
                  ),
                }}
              />
              <CoreInputField
                name="phoneNumber"
                label={t("Telephone")}
                value={formData.phoneNumber}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIphoneIcon className="text-gray" />
                    </InputAdornment>
                  ),
                }}
              />
              <CoreInputField
                name="website"
                label={t("Website")}
                value={formData.website}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LanguageIcon className="text-gray" />
                    </InputAdornment>
                  ),
                }}
              />
              {/* <CoreInputField
                name="vat"
                label="Partita IVA"
                value={formData.vat}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BadgeIcon className="text-gray" />
                    </InputAdornment>
                  ),
                }}
              /> */}
            </div>
            <div className="flex flex-col w-1/2">
              <div className="flex flex-col w-full">
                <h3 className="font-bold m-0">{t("StudioDescription")}</h3>
                <CoreTextArea
                  name="description"
                  label={t("Description")}
                  rows={5}
                  value={formData.description}
                  onChange={handleChange}
                  className="mt-3 mb-6"
                />
              </div>
              <h3 className="font-bold m-0">{t("YourColors")}</h3>
              <p>{t("EditYourColors")}</p>
              <div className="mt-1 flex justify-between w-full">
                <CoreInputField
                  name="primaryColor"
                  label={t("PrimaryColor")}
                  type="color"
                  value={formData.primaryColor || "#ffffff"}
                  onChange={handleChange}
                  focused
                  color="grey"
                  className="w-1/2"
                />
                <CoreInputField
                  name="secondaryColor"
                  label={t("SecondaryColor")}
                  type="color"
                  value={formData.secondaryColor || "#ffffff"}
                  onChange={handleChange}
                  focused
                  color="grey"
                  className="w-1/2"
                />
              </div>
              <div className="text-start mt-4 space-x-4">
                <CoreButton
                  testo="reset"
                  className="btn-round-outline"
                  onClick={handleResetColor}
                />
              </div>
            </div>
          </div>

          <div className="w-1/2 mt-12">
            <CoreButton
              testo={isLoadingEdit ? t("Wait") : t("Save")}
              className="btn-round"
              onClick={handleSubmit}
              disabled={isLoadingEdit}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalizzaStudio;
